import { put, take, fork, call, all, race, delay, spawn, select } from 'redux-saga/effects'

import { handleRequest } from './requests'

import { generateUuid } from '../services/uuid'
import { STATE_KEYS } from '../state/requests'
import { createGetRequestPagination } from '../state'
import {
    getRegistrationForm,
    createRegistrationForm,
    updateRegistrationForm,
    GetRegistrationFormRequest,
    CreateRegistrationFormRequest,
    UpdateRegistrationFormRequest,
    RegistrationForm,
    GetRegistrationFormResponse,
    GetRegistrationResultsRequest,
    getRegistrationResultsList,
    RegistrationResultsResponse,
    RequestExportRegistrationResultsPayload,
    exportRegistrationResults,
    massRegistration,
    MassRegistrationResponse,
    MassApproveRegistrationByUsernameResponse,
    massApproveByUsername,
    MassApproveByUsernameRequest,
    MassApproveByUsernameError,
    savePriorityListOrdering,
    updateWaitlistOrderMode,
    updateSchedule,
    UpdateRegistrationMarkAsViewed,
    updateMarkAsViewed,
    highlightRegistration,
    getRegistrationResultFilters,
    reorderWaitlist,
    ReorderWaitlistPayload,
} from 'services/api/registrations'
import {
    RegistrationsActions,
    saveForm,
    saveRegistrationsList,
    MassRegistrationPayload,
    MassRegistrationResult,
    MassApproveResult,
    saveMassApproveProgress,
    saveMassApproveResult,
    savePriorityList,
    PriorityListOrderingPayload,
    updatePriorityListOrdering,
    updateMassWaitlistToPriorityListResult,
    updateMassPriorityListResult,
    UpdateWaitlistOrderModePayload,
    WaitlistOrderModePayload,
    saveWaitlistOrderMode,
    UpdateRegistrationScheduleRequestPayload,
    HighlightRegistrationPayload,
    getRegistrationsList,
    clearRegistrationsList,
} from 'state/registrations'
import { MASS_REGISTRATION_ACTION_NAME_ENUM } from 'state/review'
import i18n from 'config/i18n'
import { showSnackBar, showToast } from 'services/toast'
import { closeModal } from 'state/constants'
import { savePartiposterProfiles } from 'state/partiposter'
import { separatePartiposterProfile } from 'services/partiposter'
import { getCampaignSummary } from 'state/campaigns'
import { checkForError } from 'services/api/errors'
import { MAX_APPROVE_CHUNK } from 'components/Shared/ListApprovePopUp'
import { ERROR_CODES, resetCampaignEditorErrors, saveCampaignEditorErrors } from 'state/error'
import { saveSubmissionFilters } from 'state/filter'
import { SubmissionFiltersResponse } from 'services/api/campaigns'

export function* handleGetRegistrationForm(payload: GetRegistrationFormRequest): any {
    yield handleRequest<GetRegistrationFormRequest, GetRegistrationFormResponse>({
        stateKey: STATE_KEYS.GetRegistrationForm,
        payload,
        ApiHandler: getRegistrationForm,
        *onSuccess(response) {
            yield put(
                saveForm(
                    response.data &&
                        response.data.data &&
                        response.data.data.results &&
                        response.data.data.results.length > 0
                        ? response.data.data.results[0]
                        : null,
                ),
            )
        },
    })
}

export function* watchGetCampaignRequestFlow(): any {
    while (true) {
        const action = yield take(RegistrationsActions.GET_FORM_REQUEST)
        yield fork(handleGetRegistrationForm, action.payload)
    }
}

export function* handleCreateRegistrationForm(payload: CreateRegistrationFormRequest): any {
    yield handleRequest<CreateRegistrationFormRequest, RegistrationForm>({
        stateKey: STATE_KEYS.CreateRegistrationForm,
        payload,
        ApiHandler: createRegistrationForm,
        *onSuccess(response) {
            if (payload.onSuccessCallback) {
                payload.onSuccessCallback()
            } else {
                showSnackBar({
                    title: i18n.t('successMessage.saveChanges'),
                    type: 'positive',
                })
            }
            yield put(resetCampaignEditorErrors())
            yield put(saveForm(response.data !== undefined ? response.data.data : null))
        },
        *onError(response) {
            let title = i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext')
            if (response && Object.keys(response.validation_errors).length > 0) {
                const errors = response.validation_errors
                title = i18n.t('errorMessage.validation.enterDetailsRedSubtext')
                yield put(resetCampaignEditorErrors())
                yield put(saveCampaignEditorErrors(errors))
            }
            yield call(showSnackBar, {
                title,
                type: 'error',
            })
        },
    })
}

export function* watchCreateCampaignRequestFlow(): any {
    while (true) {
        const action = yield take(RegistrationsActions.CREATE_FORM_REQUEST)
        yield fork(handleCreateRegistrationForm, action.payload)
    }
}

export function* handleUpdateRegistrationForm(payload: UpdateRegistrationFormRequest): any {
    yield handleRequest<UpdateRegistrationFormRequest, RegistrationForm>({
        stateKey: STATE_KEYS.UpdateRegistrationForm,
        payload,
        ApiHandler: updateRegistrationForm,
        *onSuccess(response) {
            if (payload.onSuccessCallback) {
                payload.onSuccessCallback()
            } else {
                showSnackBar({
                    title: i18n.t('successMessage.saveChanges'),
                    type: 'positive',
                })
            }
            yield put(resetCampaignEditorErrors())
            yield put(saveForm(response.data !== undefined ? response.data.data : null))
        },
        *onError(response) {
            let title = i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext')

            if (response && Object.keys(response.validation_errors).length > 0) {
                const errors = response.validation_errors
                title = i18n.t('errorMessage.validation.enterDetailsRedSubtext')
                yield put(resetCampaignEditorErrors())
                yield put(saveCampaignEditorErrors({ registration: errors }))
            }
            yield call(showSnackBar, {
                title,
                type: 'error',
            })
        },
    })
}

export function* watchUpdateCampaignRequestFlow(): any {
    while (true) {
        const action = yield take(RegistrationsActions.UPDATE_FORM_REQUEST)
        yield fork(handleUpdateRegistrationForm, action.payload)
    }
}

export function* handleUpdateWaitlistOrderMode(payload: UpdateWaitlistOrderModePayload): any {
    yield handleRequest<UpdateWaitlistOrderModePayload, WaitlistOrderModePayload>({
        stateKey: STATE_KEYS.UpdateWaitlistOrderMode,
        payload,
        ApiHandler: updateWaitlistOrderMode,
        *onSuccess(response) {
            yield put(saveWaitlistOrderMode(payload))
        },
    })
}

export function* watchWaitlistOrderModeFlow(): any {
    while (true) {
        const action = yield take(RegistrationsActions.UPDATE_WAITLIST_ORDER_MODE)
        yield fork(handleUpdateWaitlistOrderMode, action.payload)
    }
}

export function* handleGetResultFilters(payload: GetRegistrationResultsRequest): any {
    yield handleRequest<GetRegistrationResultsRequest, SubmissionFiltersResponse>({
        stateKey: STATE_KEYS.SkipLoading,
        payload,
        ApiHandler: getRegistrationResultFilters,
        *onSuccess(response) {
            if (response.data !== undefined) {
                yield put(saveSubmissionFilters(response.data.data.results))
            }
        },
    })
}

export function* watchGetResultFiltersFlow(): any {
    // Run filter request together with the list request
    while (true) {
        const action = yield take(RegistrationsActions.REGISTRATION_RESULTS_LIST_REQUEST)
        yield race({
            task: call(handleGetResultFilters, action.payload),
            cancelAction: take(RegistrationsActions.CLEAR_REGISTRATION_RESULTS_LIST),
        })
    }
}

export function* handleGetResultsList(payload: GetRegistrationResultsRequest): any {
    const { loadMore } = payload
    const pagination = yield select(createGetRequestPagination(STATE_KEYS.RegistrationResultsList))
    const { hasMore, next } = pagination

    // If loadMore but no more data, skip the request
    if (loadMore && !hasMore) return

    // Use next URL if loadMore and hasMore
    const requestPayload = loadMore && hasMore ? { ...payload, url: next } : payload

    yield handleRequest<GetRegistrationResultsRequest, RegistrationResultsResponse>({
        stateKey: STATE_KEYS.RegistrationResultsList,
        payload: requestPayload,
        ApiHandler: getRegistrationResultsList,
        *onSuccess(response) {
            if (!response.data) return
            const { profileById, data } = separatePartiposterProfile(response.data.data.results)
            yield put(savePartiposterProfiles(profileById))
            if (payload.is_priority) {
                yield put(savePriorityList(data))
            } else {
                yield put(saveRegistrationsList(data))
            }
        },
    })
}

export function* watchGetResultsRequestFlow(): any {
    while (true) {
        const action = yield take(RegistrationsActions.REGISTRATION_RESULTS_LIST_REQUEST)
        yield race({
            task: call(handleGetResultsList, action.payload),
            cancelAction: take(RegistrationsActions.CLEAR_REGISTRATION_RESULTS_LIST),
        })
    }
}

export function* watchGetPriorityRequestFlow(): any {
    while (true) {
        const action = yield take(RegistrationsActions.REGISTRATION_PRIORITY_LIST_REQUEST)
        yield fork(handleGetResultsList, action.payload)
    }
}

export function* handleExportRegistrationResults(payload: RequestExportRegistrationResultsPayload): any {
    yield handleRequest<RequestExportRegistrationResultsPayload, any>({
        stateKey: STATE_KEYS.ExportRegistrationResults,
        payload,
        ApiHandler: exportRegistrationResults,
        *onSuccess() {
            yield call(showSnackBar, {
                title:
                    i18n.t('successMessage.exportSuccessfulTitle') +
                    '. ' +
                    i18n.t('successMessage.sentFileToEmailSubtitle'),
                type: 'positive',
            })
            yield put(closeModal())
        },
        *onError() {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.businessLogic.somethingWentWrongExportSubtitle'),
                type: 'error',
            })
            yield put(closeModal())
        },
    })
}

export function* watchExportRegistrationResultsRequestFlow(): any {
    while (true) {
        const action = yield take(RegistrationsActions.EXPORT_REGISTRATION_RESULTS_LIST_REQUEST)
        yield fork(handleExportRegistrationResults, action.payload)
    }
}

export function* handleMassRegistration(payload: MassRegistrationPayload): any {
    const undoId = `MASS_REGISTRATION_${generateUuid()}`
    let confirmTitle: string, confirmMsg: string, sucTitle: string, recallTitle: string, errMsg: string
    switch (payload.action_name) {
        case MASS_REGISTRATION_ACTION_NAME_ENUM.APPROVE: {
            confirmTitle = i18n.t('warningMessage.confirmApprovalsTitle ', 'Confirming Approvals')
            confirmMsg = i18n.t('warningMessage.massRegistrationsConfirmApproveSubtitle', {
                defaultValue:
                    '{{count}} registration will be approved. These Partiposters will be invited to the campaign. Click Recall to cancel approvals.',
                count: payload.uuids.length,
            })
            sucTitle = i18n.t('successMessage.registrationApprovedTitle', 'Registration Approved')

            recallTitle = i18n.t('successMessage.approvalRecalledTitle', 'Approval Recalled')
            break
        }
        case MASS_REGISTRATION_ACTION_NAME_ENUM.REJECT: {
            confirmTitle = i18n.t('warningMessage.confirmRejectionsTitle', 'Confirming Rejections')
            confirmMsg = i18n.t('warningMessage.massRegistrationsConfirmRejectSubtitle', {
                defaultValue:
                    '{{count}} registration will be rejected. These Partiposters will not be able to join the campaign again. Click Recall to cancel rejection.',
                count: payload.uuids.length,
            })
            sucTitle = i18n.t('successMessage.registrationRejectedTitle', 'Registration Rejected')

            recallTitle = i18n.t('successMessage.rejectionRecalledTitle', 'Rejection Recalled')

            break
        }
        case MASS_REGISTRATION_ACTION_NAME_ENUM.WAITLIST: {
            confirmTitle = i18n.t('warningMessage.confirmApprovalsTitle ', 'Confirming Approvals')
            confirmMsg = i18n.t('warningMessage.massRegistrationsConfirmWaitlistSubtitle', {
                defaultValue:
                    '{{count}} Partiposter has been moved to waitlist and will be approved once there’s an available registration slot',
                count: payload.uuids.length,
            })
            sucTitle = i18n.t('successMessage.registrationMovedToWaitlistTitle', 'Successfully Moved to Waitlist')

            recallTitle = i18n.t('successMessage.approvalRecalledTitle', 'Approval Recalled')

            break
        }
        case MASS_REGISTRATION_ACTION_NAME_ENUM.GIVE_PRIORITY: {
            confirmTitle = i18n.t('warningMessage.confirmApprovalsTitle ', 'Confirming Approvals')
            confirmMsg = i18n.t('warningMessage.massRegistrationsConfirmPriorityListSubtitle', {
                defaultValue:
                    '{{count}} Partiposter has been moved to the priority list and will be approved accordingly',
                count: payload.uuids.length,
            })
            sucTitle = i18n.t(
                'successMessage.massRegistrationsMovedToPriorityListTitle',
                'Successfully Moved to Priority List',
            )

            recallTitle = i18n.t('successMessage.approvalRecalledTitle', 'Approval Recalled')

            break
        }
        case MASS_REGISTRATION_ACTION_NAME_ENUM.REMOVE_PRIORITY: {
            confirmTitle = i18n.t('warningMessage.confirmApprovalsTitle ', 'Confirming Approvals')
            confirmMsg = i18n.t('warningMessage.massRegistrationsConfirmDeletePriorityListSubtitle', {
                defaultValue: '{{count}} Partiposter has been moved to waitlist',
                count: payload.uuids.length,
            })
            sucTitle = i18n.t('successMessage.registrationMovedToWaitlistTitle', 'Successfully Moved to Waitlist')

            recallTitle = i18n.t('successMessage.approvalRecalledTitle', 'Approval Recalled')

            break
        }
        case MASS_REGISTRATION_ACTION_NAME_ENUM.REVERT_WAITLISTED_TO_PENDING: {
            confirmTitle = i18n.t('warningMessage.confirmApprovalsTitle ', 'Confirming Approvals')
            confirmMsg = i18n.t('warningMessage.massRegistrationsConfirmPendingSubtitle', {
                defaultValue: '{{count}} Partiposter has been moved to Pending status',
                count: payload.uuids.length,
            })
            sucTitle = i18n.t(
                'successMessage.massRegistrationsWaitlistMovedToPendingTitle',
                'Successfully Moved to Pending',
            )
            recallTitle = i18n.t('successMessage.approvalRecalledTitle', 'Approval Recalled')

            break
        }
        case MASS_REGISTRATION_ACTION_NAME_ENUM.REVERT_USER_ACCEPTED_TO_PENDING:
        case MASS_REGISTRATION_ACTION_NAME_ENUM.REVERT_APPROVED_TO_PENDING: {
            confirmTitle = i18n.t('warningMessage.confirmApprovalsTitle ', 'Confirming Approvals')
            confirmMsg = i18n.t('warningMessage.massRegistrationsConfirmPendingSubtitle', {
                defaultValue: '{{count}} Partiposter has been moved to Pending status',
                count: payload.uuids.length,
            })
            sucTitle = i18n.t(
                'successMessage.massRegistrationsWaitlistMovedToPendingTitle',
                'Successfully Moved to Pending',
            )
            recallTitle = i18n.t('successMessage.approvalRecalledTitle', 'Approval Recalled')

            break
        }
        default: {
            confirmTitle = 'confirm title'
            confirmMsg = 'confirm message'
            sucTitle = 'success title'
            recallTitle = 'recall title'
            break
        }
    }

    showToast({
        title: confirmTitle,
        message: confirmMsg,
        undoAction: { countdown: 3000, undoId },
        toastType: 'warning',
    })
    const { undoRequested, undoNotRequested, skipUndo } = yield race({
        undoRequested: take((action: any) => action.type === 'requests/UNDO' && action.payload.undoId === undoId),
        undoNotRequested: delay(3000),
        skipUndo: take((action: any) => action.type === 'requests/SKIPUNDO' && action.payload.undoId === undoId),
    })
    if (undoRequested) {
        showSnackBar({
            title: recallTitle,
            type: 'positive',
        })
    }
    if (undoNotRequested || skipUndo) {
        yield handleRequest<MassRegistrationPayload, MassRegistrationResponse>({
            stateKey: STATE_KEYS.MassRegistration,
            payload,
            ApiHandler: massRegistration,
            *onSuccess(response) {
                const massRegistrationResult: MassRegistrationResult = {
                    ...payload,
                    data: response.data !== undefined ? response.data.data : undefined,
                }
                const registrationListPayload = payload.getRegistrationListPayload

                if (massRegistrationResult.data.errors.length > 0) {
                    switch (payload.action_name) {
                        case MASS_REGISTRATION_ACTION_NAME_ENUM.APPROVE: {
                            const successCount = massRegistrationResult.data.success_count
                            const errorCount = massRegistrationResult.data.errors.length
                            // Success + error message
                            if (successCount > 0) {
                                errMsg = i18n.t('errorMessage.general.errorMessage.general.refreshPageSubtitle', {
                                    errorCount,
                                    successCount,
                                })
                            } else {
                                // If all are errors display mixed or specific error message
                                errMsg = i18n.t('errorMessage.general.refreshPageSubtitle')
                            }
                            const errorCodes = new Set(
                                massRegistrationResult.data.errors.map(
                                    (error: { error: string; uuid: string }) => error?.error,
                                ),
                            )
                            // If there is only one type of error, we specify the error message for each
                            if (errorCodes.size === 1) {
                                const [errorCode] = errorCodes
                                switch (errorCode) {
                                    case ERROR_CODES.REGISTRATION_RESULT_HAS_BEEN_REVIEWED:
                                        errMsg = i18n.t(
                                            'errorMessage.businessLogic.registrationAlreadyReviewedSubtitle',
                                        )
                                        break
                                    case ERROR_CODES.SUBMISSION_DELETED:
                                        errMsg = i18n.t('errorMessage.businessLogic.registrationAlreadyDeletedSubtitle')
                                        break
                                    case ERROR_CODES.REGISTRATION_SLOT_INSUFFICIENT:
                                        errMsg = i18n.t(
                                            'errorMessage.businessLogic.registrationSlotsAlreadyFullSubtitle',
                                        )
                                        break
                                    case ERROR_CODES.CAMPAIGN_EARLIEST_STEP_HAS_BEEN_PASSED:
                                        errMsg = i18n.t('errorMessage.businessLogic.firstStepDealineOverSubtitle')
                                        break
                                    case ERROR_CODES.REGISTRATION_RESULT_CANNOT_BE_APPROVED_DUE_TO_BLOCKED_CAMPAIGN:
                                        errMsg = i18n.t(
                                            'errorMessage.businessLogic.registrationApprovedInConflictingCampaignSubtitle',
                                        )
                                        break
                                }
                            } else {
                                errMsg = i18n.t('errorMessage.general.tryAgainContactAccountManagerSubtitle')
                            }
                            break
                        }
                        case MASS_REGISTRATION_ACTION_NAME_ENUM.REJECT: {
                            errMsg = i18n.t('', {
                                defaultValue:
                                    '{{successCount}} Registration Rejected, {{errorCount}} Registration Failed',
                                successCount: massRegistrationResult.data.success_count,
                                errorCount: massRegistrationResult.data.errors.length,
                            })
                            break
                        }
                        default: {
                            errMsg = i18n.t('errorMessage.general.unknownError')
                            break
                        }
                    }
                    showSnackBar({
                        title: errMsg,
                        type: 'error',
                    })
                } else {
                    showSnackBar({
                        type: 'positive',
                        title: sucTitle,
                    })
                }

                if (payload.action_name === MASS_REGISTRATION_ACTION_NAME_ENUM.GIVE_PRIORITY) {
                    yield put(updateMassWaitlistToPriorityListResult(massRegistrationResult))
                    if (registrationListPayload) {
                        yield put(
                            getRegistrationsList({
                                campaignUuid: payload.campaignUuid,
                                ...registrationListPayload,
                                is_priority: false,
                            }),
                        )
                    }
                } else if (payload.action_name === MASS_REGISTRATION_ACTION_NAME_ENUM.REMOVE_PRIORITY) {
                    yield put(clearRegistrationsList())
                    yield put(updateMassPriorityListResult(massRegistrationResult))
                    if (registrationListPayload) {
                        yield put(
                            getRegistrationsList({
                                campaignUuid: payload.campaignUuid,
                                ...registrationListPayload,
                            }),
                        )
                    }
                } else if (
                    payload.action_name === MASS_REGISTRATION_ACTION_NAME_ENUM.REVERT_WAITLISTED_TO_PENDING &&
                    payload.dataType &&
                    payload.dataType === 'priorityList'
                ) {
                    yield put(updateMassPriorityListResult(massRegistrationResult))
                } else {
                    if (registrationListPayload) {
                        yield put(clearRegistrationsList())
                        yield put(
                            getRegistrationsList({
                                campaignUuid: payload.campaignUuid,
                                ...registrationListPayload,
                                is_priority: false,
                            }),
                        )
                    }
                }
                yield put(getCampaignSummary({ uuid: payload.campaignUuid }))
            },
            *onError(error) {
                const isExceedMaximumPriorityListNumberError = checkForError(
                    error ? error : undefined,
                    'REGISTRATION_RESULTS_INVALID',
                    'REGISTRATION_PRIORITY_WAITLISTED_SLOT_INSUFFICIENT',
                )
                const isSlotsInsufficientError = checkForError(
                    error ? error : undefined,
                    'REGISTRATION_RESULTS_INVALID',
                    'REGISTRATION_SLOT_INSUFFICIENT',
                )

                if (isExceedMaximumPriorityListNumberError) {
                    showSnackBar({
                        title: i18n.t(
                            'errorMessage.validation.priorityListExceedLimitTitle',
                            'You can only have up to 20 Partiposters in the priority list',
                        ),
                        type: 'error',
                    })
                } else if (isSlotsInsufficientError) {
                    showSnackBar({
                        title: i18n.t('errorMessage.businessLogic.registrationSlotsAlreadyFullSubtitle'),
                        type: 'error',
                    })
                } else {
                    showSnackBar({
                        title: i18n.t(
                            'errorMessage.general.somethingWentWrongConfirmActionTitle',
                            'Something went wrong while confirming your action',
                        ),
                        type: 'error',
                    })
                }
            },
        })
    }
}

export function* watchMassRegistrationFlow(): any {
    while (true) {
        const action = yield take(RegistrationsActions.MASS_REGISTRATION)
        yield spawn(handleMassRegistration, action.payload)
    }
}

export function* handleMassApproveByUsername(payload: MassApproveByUsernameRequest): any {
    const { usernames: usernameToChunk, ...updatedPayload } = payload
    let processedCount = 0
    const totalCount = usernameToChunk.length
    let errorMessages: MassApproveByUsernameError[] = []
    yield put(saveMassApproveProgress({ count: processedCount, total: totalCount }))
    const chunkedUsernames = usernameToChunk.reduce<string[][]>((resultArray, item, index) => {
        const chunkIndex = Math.floor(index / MAX_APPROVE_CHUNK)

        if (!resultArray[chunkIndex]) {
            resultArray[chunkIndex] = [] // start a new chunk
        }

        resultArray[chunkIndex].push(item)

        return resultArray
    }, [])

    yield all(
        chunkedUsernames.map(usernames =>
            handleRequest<MassApproveByUsernameRequest, MassApproveRegistrationByUsernameResponse>({
                stateKey: STATE_KEYS.MassApproveRegistrationByUsername,
                payload: { ...updatedPayload, usernames },
                ApiHandler: massApproveByUsername,
                *onSuccess(response) {
                    processedCount += usernames.length
                    yield put(saveMassApproveProgress({ count: processedCount, total: totalCount }))
                    if (response.data !== undefined && response.data.data.errors.length > 0) {
                        errorMessages = [...errorMessages, ...response.data.data.errors]
                    }
                    // last chunk
                    if (processedCount === usernameToChunk.length) {
                        const success: MassApproveResult[] = []
                        const failed: MassApproveResult[] = []
                        usernameToChunk.map(username => {
                            const errorUsername = errorMessages.find(
                                item => item.username.toLowerCase() === username.toLowerCase(),
                            )
                            if (errorUsername) {
                                failed.push({
                                    username,
                                    message: errorUsername.error,
                                })
                            } else {
                                success.push({
                                    username,
                                })
                            }
                        })
                        yield put(saveMassApproveResult({ success, failed }))
                        setTimeout(() => {
                            payload.callback(3)
                        }, 1000)
                    }
                },
                *onError(response) {
                    setTimeout(() => {
                        payload.callback(1)
                    }, 1000)
                },
            }),
        ),
    )
}

export function* watchMassApproveByUsernameFlow(): any {
    while (true) {
        const action = yield take(RegistrationsActions.MASS_APPROVE_BY_USERNAME)
        yield fork(handleMassApproveByUsername, action.payload)
    }
}

export function* handleSavePriorityListOrdering(payload: PriorityListOrderingPayload): any {
    yield put(updatePriorityListOrdering(payload))
    yield handleRequest<PriorityListOrderingPayload, RegistrationForm>({
        stateKey: STATE_KEYS.SavePriorityListOrdering,
        payload,
        ApiHandler: savePriorityListOrdering,
        *onSuccess(response) {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.saveChanges', 'Changes Have Been Saved Successfully'),
                type: 'positive',
            })
        },
    })
}

export function* watchSavePriorityListOrderingFlow(): any {
    while (true) {
        const action = yield take(RegistrationsActions.SAVE_PRIORITY_LIST_ORDERING)
        yield fork(handleSavePriorityListOrdering, action.payload)
    }
}

export function* handleUpdateRegistrationSchedule(payload: UpdateRegistrationScheduleRequestPayload): any {
    yield handleRequest<UpdateRegistrationScheduleRequestPayload, RegistrationForm>({
        stateKey: STATE_KEYS.UpdateRegistrationForm,
        payload,
        ApiHandler: updateSchedule,
        *onSuccess(response) {
            yield put(saveForm(response.data !== undefined ? response.data.data : null))
            yield call(showSnackBar, {
                title: i18n.t('successMessage.saveChanges', 'We’ve saved your changes'),
                type: 'positive',
            })
        },
        *onError(response) {
            if (response && Object.keys(response.validation_errors).length > 0) {
                const errors = response.validation_errors
                yield put(resetCampaignEditorErrors())
                yield put(saveCampaignEditorErrors({ registration: errors }))
                yield call(showSnackBar, {
                    title: i18n.t('errorMessage.validation.enterDetailsRedSubtext'),
                    type: 'error',
                })
            } else {
                yield call(showSnackBar, {
                    title: i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext'),
                    type: 'error',
                })
            }
        },
    })
}

export function* watchUpdateRegistrationScheduleRequestFlow(): any {
    while (true) {
        const action = yield take(RegistrationsActions.UPDATE_SCHEDULE_REQUEST)
        yield fork(handleUpdateRegistrationSchedule, action.payload)
    }
}

export function* handleUpdateMarkAsViewed(payload: { campaignUuid: string; registrationId: string }): any {
    if (payload.registrationId) {
        const viewedUuid = [payload.registrationId]
        const submitPayload = { campaignUuid: payload.campaignUuid, uuids: viewedUuid }
        yield handleRequest<UpdateRegistrationMarkAsViewed, RegistrationForm>({
            stateKey: STATE_KEYS.UpdateRegistrationMarkAsViewed,
            payload: submitPayload,
            ApiHandler: updateMarkAsViewed,
        })
    }
}

export function* watchUpdateMarkAsViewed(): any {
    while (true) {
        const action = yield take(RegistrationsActions.SYNCED_VIEWED_ROWS)
        yield fork(handleUpdateMarkAsViewed, action.payload)
    }
}

export function* handleHighlightRegistration(payload: {
    campaignUuid: string
    registrationUuid: string
    highlighted: boolean
}): any {
    if (payload.registrationUuid) {
        yield handleRequest<HighlightRegistrationPayload, RegistrationForm>({
            stateKey: STATE_KEYS.HighlightRegistration,
            payload: payload,
            ApiHandler: highlightRegistration,
        })
    }
}

export function* watchHighlightRegistration(): any {
    while (true) {
        const action = yield take(RegistrationsActions.HIGHLIGHT_REGISTRATION)
        yield fork(handleHighlightRegistration, action.payload)
    }
}

export function* handleReorderWaitlist(payload: ReorderWaitlistPayload): any {
    yield handleRequest<ReorderWaitlistPayload, undefined>({
        stateKey: STATE_KEYS.ReorderWaitlist,
        payload,
        ApiHandler: reorderWaitlist,
        *onSuccess() {
            yield call(showSnackBar, {
                title: i18n.t('successMessage.saveChanges', 'Changes Have Been Saved Successfully'),
                type: 'positive',
            })
        },
        *onError() {
            yield call(showSnackBar, {
                title: i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext'),
                type: 'error',
            })
        },
    })
}

export function* watchReorderWaitlist(): any {
    while (true) {
        const action = yield take(RegistrationsActions.REORDER_WAITLIST)
        yield fork(handleReorderWaitlist, action.payload)
    }
}
